/**
 * Mobile-friendly URL builders shared by ledger cards, voucher detail, and
 * the Vouchers list. Pure functions — no React, easy to unit-test, no DOM
 * mutation.
 *
 *   • telLink(phone)            → "tel:+919876543210"   (sanitises non-digits)
 *   • whatsappLink(phone, msg)  → "https://wa.me/919876543210?text=..."
 *   • upiPayLink({vpa, payee, amount, txn, note})
 *                                → "upi://pay?pa=...&pn=...&am=...&tr=...&tn=..."
 *
 * The UPI deep link follows BHIM/NPCI spec — supported by every Indian UPI
 * app (PhonePe, Google Pay, Paytm, BHIM, Amazon Pay, …). On desktop it does
 * nothing; on mobile it opens the chooser sheet to the user's default UPI
 * app pre-filled.
 */

/** Strip every non-digit and normalise to country-code-prefixed form.
 *  Indian numbers without country code are assumed to be +91. */
export function normalisePhone(raw) {
  const d = String(raw || "").replace(/\D+/g, "");
  if (!d) return "";
  if (d.length === 10) return `91${d}`;            // Indian mobile sans CC
  if (d.length === 11 && d.startsWith("0")) return `91${d.slice(1)}`;  // 0-prefixed
  return d;                                         // Trust caller for international
}

export function telLink(phone) {
  const n = normalisePhone(phone);
  return n ? `tel:+${n}` : "";
}

export function whatsappLink(phone, message = "") {
  const n = normalisePhone(phone);
  if (!n) return "";
  const text = encodeURIComponent(message);
  return `https://wa.me/${n}${text ? `?text=${text}` : ""}`;
}

/** Generate a BHIM/UPI deep-link for collecting payment on an invoice.
 *  Required: vpa (e.g. "acme@hdfcbank"), payee (display name).
 *  Optional: amount, txnRef (invoice number — appears in payer's history),
 *  note (free text shown to payer).
 */
export function upiPayLink({ vpa, payee, amount, txnRef, note } = {}) {
  if (!vpa || !payee) return "";
  const params = new URLSearchParams();
  params.set("pa", vpa);              // payee VPA
  params.set("pn", payee);            // payee name
  if (amount !== undefined && amount !== null && Number(amount) > 0) {
    params.set("am", Number(amount).toFixed(2));
  }
  if (txnRef) params.set("tr", String(txnRef).slice(0, 35));   // ≤35 chars per spec
  if (note) params.set("tn", String(note).slice(0, 80));
  params.set("cu", "INR");
  return `upi://pay?${params.toString()}`;
}

/** Default body for a "send invoice via WhatsApp" share. */
export function invoiceWhatsAppMessage({ companyName, voucherNo, total, link }) {
  const lines = [];
  if (companyName) lines.push(`Invoice from *${companyName}*`);
  if (voucherNo) lines.push(`No: ${voucherNo}`);
  if (total !== undefined) lines.push(`Amount: ₹${Number(total).toFixed(2)}`);
  if (link) lines.push(`View: ${link}`);
  lines.push("");
  lines.push("Thanks for your business.");
  return lines.join("\n");
}

/** Web Share API wrapper with a graceful fallback to clipboard copy. */
export async function shareOrCopy({ title, text, url }) {
  // Web Share API works on iOS Safari, Android Chrome — perfect for PWA.
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return { ok: true, via: "share" };
    } catch (err) {
      if (err.name === "AbortError") return { ok: false, via: "share", aborted: true };
      // fall through to clipboard
    }
  }
  const payload = [text, url].filter(Boolean).join("\n");
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(payload);
      return { ok: true, via: "clipboard" };
    } catch (err) {
      // ignore
    }
  }
  return { ok: false, via: "none" };
}
